import React, { useCallback, useState } from 'react';
import { Panel } from 'rsuite';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';

import NavigationBar from '../Shared/NavigationBar';
import RulesSidebarNav from './RulesSidebarNav';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import useScrollToTop from '../../helpers/useScrollToTop';
import { LAYERS } from '../../helpers/rulesData';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 24,
  headerMarginBottom: 24,
  titleMarginBottom: 8,
  sectionMarginBottom: 32,
  panelMarginBottom: 16,
  sublayerMarginBottom: 12,
  sublayerPaddingLeft: 20,
  exampleMarginBottom: 12,
  examplePadding: 16,
  exampleBorderRadius: 6,
  exampleBorderLeft: 3,
  sidebarWidth: 220,
  sidebarGap: 32,
  sidebarPadding: 16,
  sidebarBorderRadius: 6,
  sidebarTitleMarginBottom: 8,
  sidebarItemPadding: '6px 0',
  stickyTop: 20,
  backLinkMarginBottom: 16,
  backLinkPadding: '10px 0',
  diagramPadding: 24,
  diagramBorderRadius: 8,
  diagramArrowSize: 16,
  diagramItemPadding: '10px 16px',
  diagramItemBorderRadius: 6,
  diagramItemMarginBottom: 4,
  layerNumberSize: 36,
  layerNumberBorderRadius: 8,
  layerNumberMarginRight: 16,
  introPadding: 20,
  introBorderRadius: 8,
  advancedPadding: 20,
  advancedBorderRadius: 8,
  cardLinkPadding: '1px 4px',
  cardLinkBorderRadius: 3,
  exampleLabelMarginBottom: 6,
  exampleScenarioMarginBottom: 8,
  paragraphMarginBottom: 12,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  subtitle: 15,
  subtitleMobile: 14,
  sidebarTitle: 14,
  sidebarTitleWeight: 600,
  sidebarTitleLetterSpacing: 1,
  sidebarItem: 14,
  layerNumber: 18,
  layerNumberWeight: 700,
  layerName: 20,
  layerNameWeight: 600,
  layerDescription: 14,
  layerDescriptionLineHeight: 1.7,
  sublayerTitle: 16,
  sublayerTitleWeight: 600,
  sublayerDescription: 14,
  sublayerDescriptionLineHeight: 1.6,
  exampleLabel: 14,
  exampleLabelWeight: 600,
  exampleLabelLetterSpacing: 1,
  exampleScenario: 14,
  exampleScenarioWeight: 600,
  exampleResolution: 14,
  exampleResolutionLineHeight: 1.6,
  diagramItem: 14,
  diagramItemWeight: 600,
  diagramArrow: 16,
  introText: 14,
  introTextLineHeight: 1.7,
  sectionHeading: 22,
  sectionHeadingWeight: 600,
  advancedText: 14,
  advancedTextLineHeight: 1.7,
  advancedSubheading: 16,
  advancedSubheadingWeight: 600,
  backLink: 14,
  cardLink: 14,
};

const CARD_NAME_REGEX = /\b(Clone|Tarmogoyf|Clever Impersonator|Baneslayer Angel|Pacifism|Shivan Dragon|Control Magic|Homeward Path|Bribery|Agent of Treachery|Magical Hack|Sleight of Mind|White Knight|Blood Moon|Steam Vents|Humility|Animate Dead|Craterhoof Behemoth|Painter's Servant|Darkest Hour|Rancor|Akroma, Angel of Wrath|Sudden Spoiling|Giant Growth|Grizzly Bears|Inside Out|Bear Cub|Dryad of the Ilysian Grove|Urborg, Tomb of Yawgmoth|Opalescence|Overwhelming Splendor)\b/g;

const renderTextWithCardLinks = (text, colors) => {
  const parts = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(CARD_NAME_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const cardName = match[0];
    parts.push(
      <Link
        key={`${cardName}-${match.index}`}
        to={`/cardsearch/${encodeURIComponent(cardName)}`}
        style={{
          color: colors.accentPrimary,
          backgroundColor: colors.accentBg,
          padding: SPACING.cardLinkPadding,
          borderRadius: SPACING.cardLinkBorderRadius,
          fontSize: FONT.cardLink,
          textDecoration: 'none',
        }}
      >
        {cardName}
      </Link>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};

const SIDEBAR_ITEMS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'diagram', label: 'Layer Order Diagram' },
  ...LAYERS.map((layer) => ({
    id: `layer-${layer.number}`,
    label: `Layer ${layer.number}: ${layer.name}`,
  })),
  { id: 'timestamps', label: 'Timestamp Order' },
  { id: 'dependencies', label: 'Dependencies' },
];

const LayersReference = () => {
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);
  const [activeId, setActiveId] = useState('intro');

  useScrollToTop();

  const scrollToSection = useCallback((sectionId) => {
    setActiveId(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const renderExamples = (examples, keyPrefix) =>
    examples.map((ex, idx) => (
      <div
        key={`${keyPrefix}-${idx}`}
        style={{
          backgroundColor: t.backgroundSecondary,
          borderLeft: `${SPACING.exampleBorderLeft}px solid ${t.accentSecondary}`,
          borderRadius: SPACING.exampleBorderRadius,
          padding: SPACING.examplePadding,
          marginBottom: SPACING.exampleMarginBottom,
        }}
      >
        <div
          style={{
            fontSize: FONT.exampleLabel,
            fontWeight: FONT.exampleLabelWeight,
            color: t.accentPrimary,
            textTransform: 'uppercase',
            letterSpacing: FONT.exampleLabelLetterSpacing,
            marginBottom: SPACING.exampleLabelMarginBottom,
          }}
        >
          Example {idx + 1}
        </div>
        <div
          style={{
            fontSize: FONT.exampleScenario,
            fontWeight: FONT.exampleScenarioWeight,
            color: t.textPrimary,
            marginBottom: SPACING.exampleScenarioMarginBottom,
          }}
        >
          {renderTextWithCardLinks(ex.scenario, t)}
        </div>
        <div
          style={{
            fontSize: FONT.exampleResolution,
            color: t.textSecondary,
            lineHeight: FONT.exampleResolutionLineHeight,
          }}
        >
          {renderTextWithCardLinks(ex.resolution, t)}
        </div>
      </div>
    ));

  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile
            ? SPACING.containerPaddingMobile
            : SPACING.containerPadding,
        }}
      >
        {/* Back link */}
        <div style={{ marginTop: SPACING.headerMarginTop, marginBottom: SPACING.backLinkMarginBottom }}>
          <Link
            to="/rules"
            style={{ color: t.accentPrimary, fontSize: FONT.backLink, textDecoration: 'none', display: 'inline-block', padding: SPACING.backLinkPadding }}
          >
            ← Back to Rules Reference
          </Link>
        </div>

        {/* Layout: sidebar + content */}
        <div style={{ display: 'flex', gap: isMobile ? 0 : SPACING.sidebarGap, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Sidebar Navigation - responsive layout handled by component */}
          <RulesSidebarNav
            sections={SIDEBAR_ITEMS}
            activeId={activeId}
            onSelect={scrollToSection}
          />

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Header */}
            <div style={{ marginBottom: SPACING.headerMarginBottom }}>
              <h1
                style={{
                  fontSize: isMobile ? FONT.titleMobile : FONT.title,
                  color: t.textPrimary,
                  marginBottom: SPACING.titleMarginBottom,
                }}
              >
                🧅 Layers
              </h1>
              <p style={{ fontSize: isMobile ? FONT.subtitleMobile : FONT.subtitle, color: t.textMuted, margin: 0 }}>
                The 7-layer system that determines how continuous effects interact on the battlefield
              </p>
            </div>

            {/* Introduction */}
            <div id="intro" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <div
                style={{
                  backgroundColor: t.backgroundSecondary,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.introBorderRadius,
                  padding: SPACING.introPadding,
                }}
              >
                <div style={{ fontSize: FONT.introText, color: t.textSecondary, lineHeight: FONT.introTextLineHeight }}>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    When multiple continuous effects modify the characteristics of permanents on the battlefield,
                    Magic uses a <strong style={{ color: t.textPrimary }}>layer system</strong> to determine the final
                    result. Effects are applied in order from Layer 1 through Layer 7, ensuring a consistent
                    outcome regardless of the order the effects were created.
                  </p>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    The layer system matters most when effects interact in complex ways, for example when one
                    effect changes a permanent's type and another removes its abilities. By applying effects in
                    a fixed layer order, both players can predict the result.
                  </p>
                  <p style={{ margin: 0 }}>
                    Within the same layer, effects are applied in <strong style={{ color: t.textPrimary }}>timestamp order</strong> (the
                    order they were created). If effects within the same layer have a dependency, the dependency
                    is resolved first regardless of timestamp. See the Timestamp Order and Dependencies sections below.
                  </p>
                </div>
              </div>
            </div>

            {/* Layer Order Diagram */}
            <div id="diagram" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Layer Order
              </h2>
              <div
                style={{
                  backgroundColor: t.cardBackground,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.diagramBorderRadius,
                  padding: SPACING.diagramPadding,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {LAYERS.map((layer, idx) => (
                  <React.Fragment key={layer.number}>
                    <div
                      onClick={() => scrollToSection(`layer-${layer.number}`)}
                      style={{
                        backgroundColor: t.accentBg,
                        border: `1px solid ${t.accentSecondary}`,
                        borderRadius: SPACING.diagramItemBorderRadius,
                        padding: SPACING.diagramItemPadding,
                        width: '100%',
                        maxWidth: 400,
                        textAlign: 'center',
                        cursor: 'pointer',
                        marginBottom: SPACING.diagramItemMarginBottom,
                      }}
                    >
                      <span style={{ fontSize: FONT.diagramItem, fontWeight: FONT.diagramItemWeight, color: t.textPrimary }}>
                        Layer {layer.number}: {layer.name}
                      </span>
                    </div>
                    {idx < LAYERS.length - 1 && (
                      <div style={{ fontSize: FONT.diagramArrow, color: t.textFaint, marginBottom: SPACING.diagramItemMarginBottom }}>
                        ▼
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Layer Cards */}
            {LAYERS.map((layer) => (
              <div
                key={layer.number}
                id={`layer-${layer.number}`}
                style={{ marginBottom: SPACING.sectionMarginBottom }}
              >
                <Panel
                  bordered
                  style={{ backgroundColor: t.cardBackground, borderColor: t.borderPrimary }}
                >
                  {/* Layer header */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACING.panelMarginBottom }}>
                    <div
                      style={{
                        width: SPACING.layerNumberSize,
                        height: SPACING.layerNumberSize,
                        borderRadius: SPACING.layerNumberBorderRadius,
                        backgroundColor: t.accentBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: SPACING.layerNumberMarginRight,
                        flexShrink: 0,
                      }}
                    >
                      <span style={{ fontSize: FONT.layerNumber, fontWeight: FONT.layerNumberWeight, color: t.accentPrimary }}>
                        {layer.number}
                      </span>
                    </div>
                    <h3 style={{ fontSize: FONT.layerName, fontWeight: FONT.layerNameWeight, color: t.textPrimary, margin: 0 }}>
                      {layer.name}
                    </h3>
                  </div>

                  {/* Description */}
                  <div
                    style={{
                      fontSize: FONT.layerDescription,
                      color: t.textSecondary,
                      lineHeight: FONT.layerDescriptionLineHeight,
                      marginBottom: SPACING.panelMarginBottom,
                    }}
                  >
                    {renderTextWithCardLinks(layer.description, t)}
                  </div>

                  {/* Sublayers (Layer 7 only) */}
                  {layer.sublayers && (
                    <div style={{ marginBottom: SPACING.panelMarginBottom }}>
                      <h4 style={{ fontSize: FONT.sublayerTitle, fontWeight: FONT.sublayerTitleWeight, color: t.accentPrimary, marginBottom: SPACING.sublayerMarginBottom }}>
                        Sublayers
                      </h4>
                      {layer.sublayers.map((sub) => (
                        <Panel
                          key={sub.id}
                          bordered
                          collapsible
                          defaultExpanded
                          header={
                            <span style={{ fontSize: FONT.sublayerTitle, color: t.accentPrimary }}>
                              {sub.id}: {sub.name}
                            </span>
                          }
                          style={{
                            backgroundColor: t.backgroundSecondary,
                            borderColor: t.borderPrimary,
                            marginBottom: SPACING.sublayerMarginBottom,
                            marginLeft: isMobile ? 0 : SPACING.sublayerPaddingLeft,
                          }}
                        >
                          <div
                            style={{
                              fontSize: FONT.sublayerDescription,
                              color: t.textSecondary,
                              lineHeight: FONT.sublayerDescriptionLineHeight,
                              marginBottom: sub.examples?.length > 0 ? SPACING.sublayerMarginBottom : 0,
                            }}
                          >
                            {sub.description}
                          </div>
                          {sub.examples && renderExamples(sub.examples, `sub-${sub.id}`)}
                        </Panel>
                      ))}
                    </div>
                  )}

                  {/* Examples */}
                  {layer.examples && layer.examples.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: FONT.sublayerTitle, fontWeight: FONT.sublayerTitleWeight, color: t.accentPrimary, marginBottom: SPACING.sublayerMarginBottom }}>
                        Examples
                      </h4>
                      {renderExamples(layer.examples, `layer-${layer.number}`)}
                    </div>
                  )}
                </Panel>
              </div>
            ))}

            {/* Timestamp Order */}
            <div id="timestamps" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Timestamp Order (Rule 613.7)
              </h2>
              <div
                style={{
                  backgroundColor: t.cardBackground,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.advancedBorderRadius,
                  padding: SPACING.advancedPadding,
                }}
              >
                <div style={{ fontSize: FONT.advancedText, color: t.textSecondary, lineHeight: FONT.advancedTextLineHeight, marginBottom: SPACING.panelMarginBottom }}>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    Within the same layer or sublayer, effects are applied in <strong style={{ color: t.textPrimary }}>timestamp order</strong>.
                    The effect that was created first is applied first. A continuous effect's timestamp is usually
                    the time the permanent entered the battlefield or the time the effect was created.
                  </p>
                  <p style={{ margin: 0 }}>
                    If two effects in the same layer conflict, the one with the later timestamp "wins" because
                    it overwrites the earlier one.
                  </p>
                </div>
                <h4 style={{ fontSize: FONT.advancedSubheading, fontWeight: FONT.advancedSubheadingWeight, color: t.textPrimary, marginBottom: SPACING.sublayerMarginBottom }}>
                  Example
                </h4>
                <div
                  style={{
                    backgroundColor: t.backgroundSecondary,
                    borderLeft: `${SPACING.exampleBorderLeft}px solid ${t.accentSecondary}`,
                    borderRadius: SPACING.exampleBorderRadius,
                    padding: SPACING.examplePadding,
                  }}
                >
                  <div style={{ fontSize: FONT.exampleScenario, fontWeight: FONT.exampleScenarioWeight, color: t.textPrimary, marginBottom: SPACING.exampleScenarioMarginBottom }}>
                    {renderTextWithCardLinks('Blood Moon is on the battlefield (timestamp T1). Later, Dryad of the Ilysian Grove enters (timestamp T2). Both operate in Layer 4 (type-changing effects).', t)}
                  </div>
                  <div style={{ fontSize: FONT.exampleResolution, color: t.textSecondary, lineHeight: FONT.exampleResolutionLineHeight }}>
                    {renderTextWithCardLinks(
                      'Blood Moon (T1) makes all nonbasic lands Mountains. Dryad of the Ilysian Grove (T2) makes all your lands every basic land type. ' +
                      'Since Dryad has the later timestamp, its effect is applied second, so your nonbasic lands are Mountains (from Blood Moon) ' +
                      'and also Plains, Islands, Swamps, and Forests (from Dryad). Your lands can tap for any color.',
                      t
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Dependencies */}
            <div id="dependencies" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Dependencies (Rule 613.8)
              </h2>
              <div
                style={{
                  backgroundColor: t.cardBackground,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.advancedBorderRadius,
                  padding: SPACING.advancedPadding,
                }}
              >
                <div style={{ fontSize: FONT.advancedText, color: t.textSecondary, lineHeight: FONT.advancedTextLineHeight, marginBottom: SPACING.panelMarginBottom }}>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    An effect is said to <strong style={{ color: t.textPrimary }}>depend</strong> on another effect in
                    the same layer if: (a) applying the other effect would change what the first effect applies
                    to or what it does, and (b) neither effect is a copy effect in Layer 1.
                  </p>
                  <p style={{ margin: 0 }}>
                    When a dependency exists, the effect that is depended upon is applied <strong style={{ color: t.textPrimary }}>first</strong>,
                    regardless of timestamp order. This prevents paradoxical or inconsistent results.
                  </p>
                </div>
                <h4 style={{ fontSize: FONT.advancedSubheading, fontWeight: FONT.advancedSubheadingWeight, color: t.textPrimary, marginBottom: SPACING.sublayerMarginBottom }}>
                  Example
                </h4>
                <div
                  style={{
                    backgroundColor: t.backgroundSecondary,
                    borderLeft: `${SPACING.exampleBorderLeft}px solid ${t.accentSecondary}`,
                    borderRadius: SPACING.exampleBorderRadius,
                    padding: SPACING.examplePadding,
                  }}
                >
                  <div style={{ fontSize: FONT.exampleScenario, fontWeight: FONT.exampleScenarioWeight, color: t.textPrimary, marginBottom: SPACING.exampleScenarioMarginBottom }}>
                    {renderTextWithCardLinks('Blood Moon and Urborg, Tomb of Yawgmoth are both on the battlefield. Both operate in Layer 4.', t)}
                  </div>
                  <div style={{ fontSize: FONT.exampleResolution, color: t.textSecondary, lineHeight: FONT.exampleResolutionLineHeight }}>
                    {renderTextWithCardLinks(
                      'Blood Moon\'s effect ("Nonbasic lands are Mountains") depends on Urborg\'s effect ("Each land is a Swamp in addition to its other types") because ' +
                      'applying Urborg\'s effect would change whether Blood Moon applies to Urborg itself (Urborg is a nonbasic land). ' +
                      'Since Blood Moon depends on Urborg, Urborg\'s effect is applied first. All lands become Swamps in addition to their other types. ' +
                      'Then Blood Moon is applied. All nonbasic lands (including Urborg, which is nonbasic) lose their subtypes and become Mountains. ' +
                      'Result: Urborg is a Mountain (not a Swamp). Basic lands are Swamps and their printed type. All other nonbasic lands are Mountains.',
                      t
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default LayersReference;
